/**
 * src/lib/voice/sttHallucinationFilter.js
 *
 * Rejects Whisper transcription artifacts that would otherwise land as
 * real user turns in the voice conversation pipeline. Whisper is known to
 * emit boilerplate phrases ("Thank you for watching", "Subtitles by the
 * Amara community", etc.) on near-silent or low-energy audio — these are
 * training-data leakage from YouTube captions.
 *
 * Detection rules (first match wins, cheapest first):
 *   1. Exact-match boilerplate   — transcript is literally a known artifact
 *   2. Dominant boilerplate      — a known artifact occupies ≥40% of the
 *                                  transcript by character count (handles
 *                                  "Thank you for watching. Thank you for
 *                                  watching. bimini, BOM..." where the
 *                                  boilerplate bleeds through real audio)
 *   3. Pathological repetition   — any comma/period-separated segment repeats
 *                                  3+ times AND makes up ≥50% of segments
 *                                  (catches "X. X. X. X." spam from silence
 *                                  even if X isn't in our phrase list)
 *
 * Single-word utterances ("yes", "no", "OK") always pass — legitimate
 * voice commands must not be filtered.
 *
 * Export is a pure function so callers can unit-test it in isolation and
 * wire it into the voice orchestrator's STT boundary.
 */

// Known Whisper training-data artifacts. Case-insensitive contains/equals.
// Keep this list tight — adding benign phrases will reject real speech.
export const HALLUCINATION_PHRASES = [
  'thank you for watching',
  'thanks for watching',
  'thank you for your attention',
  'subscribe to my channel',
  'please subscribe',
  'like and subscribe',
  "don't forget to subscribe",
  'amara.org',
  'subtitles by the amara.org community',
  'subtitles by',
  'captions by',
  'music playing',
  '[music]',
  '♪',
  'you you you',
  'peace to you all',
];

// Well-known Whisper TRUE-SILENCE artifacts that are too SHORT to live in
// HALLUCINATION_PHRASES — putting "you" or "." in the substring-scanned list
// (Rules 2/2b/4 use `lc.includes(phrase)`) would reject real speech, since
// almost every sentence contains "you" and ends with ".". These are matched
// EXACTLY ONLY (Rule 1, after trailing-punctuation strip) so a lone artifact
// is rejected while the same token inside a real sentence passes.
//
// Reason prefix is `silence` so the conversation pipeline can distinguish a
// "didn't catch that — try again" no-speech outcome from a content-bearing
// hallucination and avoid speaking the misleading "I couldn't understand
// what you meant by X" apology (2026-06-06 listening-ball report).
export const SILENCE_EXACT_PHRASES = [
  'thank you',
  'thanks',
  'you',
  'please subscribe',
  'subscribe',
  'bye',
  '', // bare "." / "..." collapse to empty after punctuation strip
];

function countOccurrences(haystack, needle) {
  if (!needle) return 0;
  let count = 0;
  let idx = 0;
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1;
    idx += needle.length;
  }
  return count;
}

/**
 * @param {string} text — the Whisper final transcript
 * @param {object} [opts]
 * @param {string[]} [opts.vocabTerms] — active domain vocab hints (lowercase).
 *        When provided, enables Rule 4 (vocab_echo) which detects when
 *        Whisper reflects the vocab_hints prompt back as transcript on
 *        low-energy audio — "SY Artemis — Full Refit, Muluc, Private,".
 * @returns {{ hallucination: boolean, reason?: string }}
 */
export function detectSttHallucination(text, opts = {}) {
  const raw = (text || '').trim();
  if (!raw) return { hallucination: true, reason: 'empty' };

  const lc = raw.toLowerCase();

  // Rule 1 — exact-match boilerplate (strip trailing punctuation for compare)
  const stripped = lc.replace(/[.!?,;:\s]+$/g, '');
  for (const phrase of HALLUCINATION_PHRASES) {
    if (stripped === phrase) {
      return { hallucination: true, reason: `phrase_exact:${phrase}` };
    }
  }

  // Rule 1b — exact-match TRUE-SILENCE artifacts (short tokens that must NOT
  // be substring-scanned). A lone "Thank you", "you", or "." (which strips to
  // "") is a no-speech artifact, not a real turn. Distinct `silence` reason
  // lets the conversation pipeline route to a "try again" path instead of a
  // bogus model apology.
  for (const phrase of SILENCE_EXACT_PHRASES) {
    if (stripped === phrase) {
      return { hallucination: true, reason: `silence:${phrase || 'punctuation_only'}` };
    }
  }

  // Rule 2 — dominant boilerplate: repeated occurrences of a known phrase
  // account for ≥40% of the total character budget.
  // Also: any single phrase occurring 3+ times is a hallucination regardless
  // of dominance — this is the "thank you for watching" YouTube spam pattern
  // (2026-04-21 8:07 AM screenshot) where the phrase bleeds through 4 times
  // mixed with training-data junk, never reaching the 40% char threshold but
  // still obviously hallucinated.
  for (const phrase of HALLUCINATION_PHRASES) {
    const count = countOccurrences(lc, phrase);
    if (count >= 3) {
      return { hallucination: true, reason: `phrase_count:${count}x:${phrase}` };
    }
    if (count >= 2 && (count * phrase.length) / lc.length >= 0.4) {
      return { hallucination: true, reason: `phrase_repeated:${count}x:${phrase}` };
    }
  }

  // Rule 2b — mixed-boilerplate: two OR more DISTINCT known phrases
  // co-occurring in the same transcript is strong evidence of a silence
  // hallucination (Whisper cycles through multiple training-data patterns).
  // The 2026-04-21 screenshot mixed "thank you for watching" + "thank you
  // for your attention" — each alone might not dominate, together they're
  // a clear signal.
  const distinctHits = HALLUCINATION_PHRASES.filter((p) => lc.includes(p));
  if (distinctHits.length >= 2) {
    return {
      hallucination: true,
      reason: `mixed_phrases:${distinctHits.length}:${distinctHits.slice(0, 3).join('|')}`,
    };
  }

  // Rule 3 — pathological repetition of any segment (punctuation-split)
  // Split on common sentence/clause boundaries, normalize whitespace, count.
  const segments = lc
    .split(/[.!?,;]+/)
    .map((s) => s.trim().replace(/\s+/g, ' '))
    .filter((s) => s.length > 0);

  if (segments.length >= 3) {
    const counts = new Map();
    for (const seg of segments) counts.set(seg, (counts.get(seg) || 0) + 1);
    let maxCount = 0;
    let topSeg = '';
    for (const [seg, c] of counts) {
      if (c > maxCount) { maxCount = c; topSeg = seg; }
    }
    const dominance = maxCount / segments.length;
    if (maxCount >= 3 && dominance >= 0.5) {
      return {
        hallucination: true,
        reason: `repetition:${maxCount}x:${topSeg.slice(0, 40)}`,
      };
    }
  }

  // Rule 4 — vocab_echo (90-07 AC-2): Whisper echoes the vocab_hints
  // prompt back as transcript on low-energy audio. Pattern: transcript
  // is a comma/dash-joined list of known vocab terms, e.g.
  // "SY Artemis — Full Refit, Muluc, Private,".
  //
  // Coverage heuristic: if non-whitespace/non-punctuation characters in
  // the transcript are ≥60% explained by known vocab terms, reject.
  // Benign mention of ONE vocab term in a 12-word sentence will not
  // cross the threshold; a 5-word list of 4 vocab terms will.
  // A genuine vocab echo is a multi-term LIST (e.g. "SY Artemis — Full Refit,
  // Muluc, Private,"); a short command is never one. Guard added 2026-06-11
  // after a CEO live-test rejected "test this" as `vocab_echo:200%`: the user
  // has "test" projects, and sub-4-char substrings ("is"/"hi" inside real words)
  // double-counted against a tiny transcript, pushing coverage past 100%.
  //   (1) only run for >= 4 words, and
  //   (2) ignore vocab terms shorter than 4 alpha chars (the substring noise),
  //   (3) cap reported coverage at 100% (honest reason string).
  const vocabTerms = Array.isArray(opts.vocabTerms) ? opts.vocabTerms : [];
  const wordCount = raw.split(/\s+/).filter(Boolean).length;
  if (vocabTerms.length > 0 && wordCount >= 4) {
    const alphaOnly = lc.replace(/[^a-z0-9]/g, '');
    if (alphaOnly.length > 0) {
      let vocabChars = 0;
      for (const term of vocabTerms) {
        const termLc = String(term || '').trim().toLowerCase();
        if (!termLc) continue;
        const termAlpha = termLc.replace(/[^a-z0-9]/g, '');
        if (termAlpha.length < 4) continue;
        const count = countOccurrences(alphaOnly, termAlpha);
        vocabChars += count * termAlpha.length;
      }
      const coverage = Math.min(vocabChars / alphaOnly.length, 1);
      if (coverage >= 0.6) {
        return {
          hallucination: true,
          reason: `vocab_echo:${Math.round(coverage * 100)}%`,
        };
      }
    }
  }

  return { hallucination: false };
}
